/**
 * Permission utility functions for checking user permissions
 * Permissions are in format: ObjectName.Access.CanGet or ObjectName.Method.MethodName
 */

/**
 * Check if user has access to an object with a specific permission
 * @param permissions Array of permission strings from the user
 * @param objectName Name of the object (e.g., "AuthClient", "Subscription")
 * @param accessType Type of access (e.g., "CanGet", "CanCreate", "CanUpdate", "CanDelete")
 * @returns true if user has the permission
 */
export function hasAccessToObject(
  permissions: string[],
  objectName: string,
  accessType: 'CanGet' | 'CanCreate' | 'CanUpdate' | 'CanDelete'
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  const requiredPermission = `${objectName}.Access.${accessType}`;
  return permissions.includes(requiredPermission);
}

/**
 * Check if user has access to a method on an object
 * @param permissions Array of permission strings from the user
 * @param objectName Name of the object
 * @param methodName Name of the method
 * @returns true if user has the permission
 */
export function hasAccessToMethod(
  permissions: string[],
  objectName: string,
  methodName: string
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  const requiredPermission = `${objectName}.Method.${methodName}`;
  return permissions.includes(requiredPermission);
}

/**
 * Check if user has at least one of the specified permissions
 * @param permissions Array of permission strings from the user
 * @param requiredPermissions Array of required permission strings
 * @returns true if user has at least one of the required permissions
 */
export function hasAnyPermission(
  permissions: string[],
  requiredPermissions: string[]
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  return requiredPermissions.some(required => permissions.includes(required));
}

/**
 * Check if user has all of the specified permissions
 * @param permissions Array of permission strings from the user
 * @param requiredPermissions Array of required permission strings
 * @returns true if user has all of the required permissions
 */
export function hasAllPermissions(
  permissions: string[],
  requiredPermissions: string[]
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  return requiredPermissions.every(required => permissions.includes(required));
}
